// Mappers for employee contract edit operations
// Handles conversion between transport DTOs (LC_*) and form data
import type {
  LC_GetEmployeeContractByIdOut,
  LC_UpdateEmployeeContractIn,
  ValidatedUpdateEmployeeContractFormData,
} from '../types/employeeContractEdit.types';
import type { UpdateEmployeeContractFormData } from '@/schemas/employee';


/**
 * Maps transport DTO from backend to form data structure
 * Converts backend ISO 8601 format (2021-08-04T00:00:00) to HTML5 date input format (yyyy-MM-dd)
 * 
 * @param transport - LC_GetEmployeeContractByIdOut from API
 * @returns UpdateEmployeeContractFormData for form population
 */
export function mapEmployeeContractToForm(
  transport: LC_GetEmployeeContractByIdOut,
): UpdateEmployeeContractFormData {
  /**
   * Convert ISO 8601 date string to yyyy-MM-dd for HTML5 date input
   * Example: "2021-08-04T00:00:00" -> "2021-08-04"
   */
  const formatDateForForm = (date: string): string => date.split('T')[0];

  /**
   * Convert backend 0 values to undefined for form selectors
   * Backend uses 0 for "not selected", frontend uses undefined
   */
  const zeroToUndefined = (value: number): number | undefined => 
    value === 0 ? undefined : value;

  return {
    contractNumber: transport.contractNumberREX,
    contractName: transport.contractNameREX,
    contractStatusId: transport.contractStatusIdREX,
    contractTypeId: zeroToUndefined(transport.employeeContractTypeId),
    dateEntry: formatDateForForm(transport.dateEntry),
    contractStartDate: formatDateForForm(transport.contractStartDate),
    contractEndDate: transport.contractEndDate
      ? formatDateForForm(transport.contractEndDate)
      : null,
    contractModalityId: zeroToUndefined(transport.contractModalityId),
    companyContractId: zeroToUndefined(transport.companyContractId),
    areaId: zeroToUndefined(transport.companyAreaId),
    positionId: zeroToUndefined(transport.positionId),
    costCenterId: zeroToUndefined(transport.costCenterId),
    unionCode: transport.unionCode || '', // Required field, empty string if null
  };
}

/**
 * Maps validated form data to transport DTO for API submission
 * Converts HTML5 date input format (yyyy-MM-dd) to ISO 8601 format (2021-08-04T00:00:00)
 * 
 * @param formData - ValidatedUpdateEmployeeContractFormData after schema validation
 * @returns LC_UpdateEmployeeContractIn for API request
 */
export function mapUpdateEmployeeContractFormToTransport(
  formData: ValidatedUpdateEmployeeContractFormData,
): LC_UpdateEmployeeContractIn {
  /**
   * Convert yyyy-MM-dd to ISO 8601 format for API
   * Example: "2021-08-04" -> "2021-08-04T00:00:00"
   */
  const formatDateForApi = (date: string): string => `${date}T00:00:00`;

  return {
    contractNameREX: formData.contractName,
    contractStatusIdREX: formData.contractStatusId,
    employeeContractTypeId: formData.contractTypeId,
    dateEntry: formatDateForApi(formData.dateEntry),
    contractStartDate: formatDateForApi(formData.contractStartDate),
    contractEndDate: formData.contractEndDate
      ? formatDateForApi(formData.contractEndDate)
      : null,
    contractModalityId: formData.contractModalityId,
    companyContractId: formData.companyContractId,
    positionId: formData.positionId,
    costCenterId: formData.costCenterId,
    unionCode: formData.unionCode,
  };
}
